import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { F } from "@/pages/Employees";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X, Upload, Send, Save } from "lucide-react";
import { Lampiran } from "@/components/nota/NotaShared";
import { toast } from "sonner";

const MAX = 5 * 1024 * 1024;
const TYPES = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
const blank = () => ({ nama: "", qty: 1, harga: "", keterangan: "" });
const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

const useOptions = () => {
  const [o, setO] = useState({ projects: [], employees: [], categories: [] });
  useEffect(() => {
    Promise.all([
      api.get("/projects").then((r) => r.data).catch(() => []),
      api.get("/employees?menu=/nota").then((r) => r.data.filter((e) => e.aktif)).catch(() => []),
      api.get("/nota-pengajuan-kategori").then((r) => r.data).catch(() => []),
    ]).then(([projects, employees, categories]) => setO({ projects, employees, categories }));
  }, []);
  return o;
};

const ItemRow = ({ it, k, setItem, onDel }) => (
  <div className="grid grid-cols-6 sm:grid-cols-12 gap-2 px-3 py-2 border-t items-center" data-testid={`nota-item-${k}`}>
    <Input className="col-span-6 sm:col-span-4 h-10" placeholder="Nama barang" value={it.nama} onChange={(e) => setItem(k, "nama", e.target.value)} data-testid={`nota-item-nama-${k}`} />
    <Input className="col-span-2 sm:col-span-2 h-10 text-right" type="number" inputMode="decimal" min="0" placeholder="Qty" value={it.qty} onChange={(e) => setItem(k, "qty", e.target.value)} data-testid={`nota-item-qty-${k}`} />
    <Input className="col-span-4 sm:col-span-2 h-10 text-right" type="number" inputMode="numeric" min="0" placeholder="Harga satuan" value={it.harga} onChange={(e) => setItem(k, "harga", e.target.value)} data-testid={`nota-item-harga-${k}`} />
    <div className="col-span-2 sm:col-span-2 text-right text-sm font-mono font-semibold" data-testid={`nota-item-sub-${k}`}>{rupiah(Math.round(Number(it.qty || 0) * Number(it.harga || 0)))}</div>
    <div className="col-span-4 sm:col-span-2 flex items-center gap-1">
      <Input className="h-10" placeholder="Ket." value={it.keterangan} onChange={(e) => setItem(k, "keterangan", e.target.value)} data-testid={`nota-item-ket-${k}`} />
      {onDel && <button type="button" className="text-rose-500" onClick={onDel} data-testid={`nota-item-del-${k}`}><X className="h-4 w-4" /></button>}
    </div>
  </div>
);

export default function NotaForm({ nota, onClose, onSaved, onPreview }) {
  const opt = useOptions();
  const mandors = opt.employees.filter((e) => e.is_pimpinan || /mandor/i.test(e.jabatan || "") || e.id === nota?.mandor_id);
  const [f, setF] = useState(() => nota
    ? { tanggal: nota.tanggal, project_id: nota.project_id || "", mandor_id: nota.mandor_id || "", kategori: nota.kategori || "", vendor: nota.vendor || "",
        keterangan: nota.keterangan, items: nota.items.map((i) => ({ nama: i.nama, qty: i.qty, harga: i.harga, keterangan: i.keterangan || "" })), lampiran: nota.lampiran }
    : { tanggal: todayISO(), project_id: "", mandor_id: "", kategori: "", vendor: "", keterangan: "", items: [blank()], lampiran: [] });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const total = f.items.reduce((s, i) => s + Math.round(Number(i.qty || 0) * Number(i.harga || 0)), 0);
  const setItem = (k, key, v) => setF((p) => ({ ...p, items: p.items.map((it, j) => (j === k ? { ...it, [key]: v } : it)) }));
  useEffect(() => {
    if (!nota && !f.kategori && opt.categories.length) setF((p) => ({ ...p, kategori: opt.categories[0].nama }));
  }, [opt.categories, nota, f.kategori]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    for (const file of files) {
      if (!TYPES.includes(file.type)) { toast.error(`${file.name}: hanya foto JPG/PNG/WEBP atau PDF`); continue; }
      if (file.size > MAX) { toast.error(`${file.name}: ukuran maksimal 5 MB`); continue; }
      setUploading(true);
      try {
        const fd = new FormData(); fd.append("file", file);
        const r = await api.post("/nota-pengajuan/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setF((p) => ({ ...p, lampiran: [...p.lampiran, r.data] }));
      } catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); }
    }
  };

  const save = async (ajukan) => {
    if (!f.tanggal) return toast.error("Tanggal belanja wajib diisi");
    const items = f.items.filter((i) => i.nama.trim() || Number(i.harga));
    if (ajukan) {
      if (!f.project_id) return toast.error("Pilih proyek sebelum mengajukan");
      if (!f.keterangan.trim()) return toast.error("Keterangan keperluan wajib diisi");
      if (!items.length) return toast.error("Minimal satu barang wajib diisi");
      if (items.some((i) => !i.nama.trim() || Number(i.qty) <= 0 || Number(i.harga) <= 0)) return toast.error("Lengkapi nama, qty (>0) dan harga (>0) setiap barang");
      if (!f.lampiran.length) return toast.error("Unggah foto/PDF nota sebelum mengajukan");
    }
    setBusy(true);
    try {
      const body = { ...f, items: items.map((i) => ({ nama: i.nama, qty: Number(i.qty || 0), harga: Number(i.harga || 0), keterangan: i.keterangan })), lampiran: f.lampiran.map((l) => l.path), ajukan };
      if (nota) await api.put(`/nota-pengajuan/${nota.id}`, body); else await api.post("/nota-pengajuan", body);
      toast.success(ajukan ? "Nota diajukan, menunggu persetujuan" : "Draf tersimpan");
      onSaved();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="nota-form">
        <DialogHeader><DialogTitle>{nota ? `Ubah Nota ${nota.nomor}` : "Tambah Nota Belanja"}</DialogTitle></DialogHeader>
        {nota?.status === "Ditolak" && <div className="rounded-lg bg-rose-50 p-3 text-sm text-rose-800" data-testid="nota-form-reject-note"><b>Ditolak:</b> {nota.alasan_tolak}</div>}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <F label="Tanggal"><Input type="date" max={todayISO()} value={f.tanggal} onChange={(e) => setF({ ...f, tanggal: e.target.value })} data-testid="nota-f-tanggal" /></F>
          <F label="Proyek *">
            <Select value={f.project_id} onValueChange={(v) => setF({ ...f, project_id: v })}>
              <SelectTrigger data-testid="nota-f-project"><SelectValue placeholder="Pilih proyek" /></SelectTrigger>
              <SelectContent>{opt.projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Mandor (opsional)">
            <Select value={f.mandor_id || "none"} onValueChange={(v) => setF({ ...f, mandor_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="nota-f-mandor"><SelectValue placeholder="-" /></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{mandors.map((e) => <SelectItem key={e.id} value={e.id}>{e.nama}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Kategori">
            <Select value={f.kategori} onValueChange={(v) => setF({ ...f, kategori: v })}>
              <SelectTrigger data-testid="nota-f-kategori"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
              <SelectContent>{opt.categories.map((k) => <SelectItem key={k.id} value={k.nama}>{k.nama}</SelectItem>)}</SelectContent>
            </Select>
          </F>
        </div>
        <div className="rounded-lg border border-slate-200">
          <div className="hidden sm:grid grid-cols-12 gap-2 bg-slate-50 px-3 py-2 text-xs font-bold uppercase text-slate-500">
            <div className="col-span-4">Nama Barang</div><div className="col-span-2 text-right">Qty</div>
            <div className="col-span-2 text-right">Harga Satuan</div><div className="col-span-2 text-right">Jumlah</div><div className="col-span-2">Ket.</div>
          </div>
          {f.items.map((it, k) => <ItemRow key={k} it={it} k={k} setItem={setItem} onDel={f.items.length > 1 ? () => setF({ ...f, items: f.items.filter((_, j) => j !== k) }) : null} />)}
          <div className="flex items-center justify-between px-3 py-2 border-t">
            <Button type="button" variant="outline" size="sm" onClick={() => setF({ ...f, items: [...f.items, blank()] })} data-testid="nota-add-item"><Plus className="h-4 w-4 mr-1" /> Baris</Button>
            <div className="text-sm">Total: <b className="font-mono" data-testid="nota-f-total">{rupiah(total)}</b></div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <F label="Keterangan *"><Input value={f.keterangan} maxLength={500} onChange={(e) => setF({ ...f, keterangan: e.target.value })} data-testid="nota-f-keterangan" placeholder="mis. Beli cat & thinner" /></F>
          <F label="Foto Nota (foto/PDF, maks 5 MB) *">
            <div className="flex flex-wrap items-center gap-2">
              {f.lampiran.map((l, k) => <Lampiran key={l.path} f={l} onOpen={onPreview} testid={`nota-f-lampiran-${k}`} onRemove={() => setF({ ...f, lampiran: f.lampiran.filter((_, j) => j !== k) })} />)}
              <label className="inline-flex cursor-pointer items-center gap-1 text-sm text-blue-700 underline" data-testid="nota-f-upload-label">
                <Upload className="h-4 w-4" />{uploading ? "Mengunggah…" : f.lampiran.length ? "Tambah" : "Unggah"}
                <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" multiple className="hidden" onChange={upload} data-testid="nota-f-upload" />
              </label>
            </div>
          </F>
        </div>
        <div className="sticky bottom-0 -mx-4 sm:mx-0 bg-white px-4 sm:px-0 pt-3 pb-1 grid grid-cols-2 sm:flex gap-2 sm:justify-end border-t">
          <Button variant="outline" className="h-11" disabled={busy} onClick={onClose}>Batal</Button>
          <Button variant="outline" className="h-11" disabled={busy || uploading} onClick={() => save(false)} data-testid="nota-save-draft"><Save className="h-4 w-4 mr-1" /> Simpan Draf</Button>
          <Button className="h-11 col-span-2 bg-blue-800 hover:bg-blue-900" disabled={busy || uploading} onClick={() => save(true)} data-testid="nota-submit"><Send className="h-4 w-4 mr-1" /> {busy ? "Menyimpan…" : "Ajukan"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
